import { useMemo } from 'react';
import { Box, Icon, Label, Text } from '@nimbus-ds/components';
import { EyeOffIcon } from '@nimbus-ds/icons';
import { InteractiveList } from '@nimbus-ds/patterns';
import { ImageItemStratus, Stack } from 'commons/components';
import { SelectedProductInterface } from 'domains/Catalog/Products/components/SelectProductList/types';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import VariantList from './VariantList';

interface ProductListItemProps {
  variants: SelectedProductInterface[];
  selectedVariantIds: string[];
  onAdd: (variantIds: string[]) => void;
  onRemove: (variantIds: string[]) => void;
}

function ProductListItem({
  variants,
  selectedVariantIds,
  onAdd,
  onRemove,
}: Readonly<ProductListItemProps>): JSX.Element | null {
  const t = useTranslationShipping();

  const selectedVariantsForProduct = useMemo(
    () => variants.filter((v) => selectedVariantIds.includes(String(v.id))),
    [variants, selectedVariantIds],
  );

  const isChecked = useMemo(
    () =>
      variants.length > 0 &&
      selectedVariantsForProduct.length === variants.length,
    [selectedVariantsForProduct.length, variants.length],
  );

  const isIndeterminate = useMemo(
    () =>
      selectedVariantsForProduct.length > 0 &&
      selectedVariantsForProduct.length < variants.length,
    [selectedVariantsForProduct.length, variants.length],
  );

  const hasDefaultVariant = useMemo(
    () =>
      variants.length === 1 &&
      Object.keys(variants[0]?.attributes || {}).length === 0,
    [variants],
  );

  if (variants.length === 0) {
    return null;
  }

  const productId = variants[0].id;
  const productName = variants[0].name;
  const productImageUrl = variants[0].imageUrl;
  const isHidden = !variants[0].visible;

  const handleToggle = () => {
    if (isChecked || isIndeterminate) {
      const variantIdsToRemove = variants
        .filter((v) => selectedVariantIds.includes(String(v.id)))
        .map((v) => String(v.id));
      onRemove(variantIdsToRemove);
    } else {
      const variantIdsToAdd = variants
        .filter((v) => !selectedVariantIds.includes(String(v.id)))
        .map((v) => String(v.id));
      onAdd(variantIdsToAdd);
    }
  };

  return (
    <InteractiveList.CheckboxItem
      title=""
      showTitle={false}
      checkbox={{
        name: 'checkbox-element',
        onChange: handleToggle,
        indeterminate: isIndeterminate,
        checked: isChecked,
      }}
    >
      <Stack justify="space-between" align="flex-start">
        <Box flex="3">
          <ImageItemStratus
            id={productId}
            title={productName}
            imageUrl={productImageUrl}
            imageWidth="56px"
            appearance="default"
            titleAppearance="neutral-textLow"
            titleSize="base"
            bold
          >
            {isHidden && (
              <Box paddingY="1">
                <Label id={productId} color="warning">
                  <Icon source={<EyeOffIcon size="small" />} />
                  <Text>
                    {t(
                      'deliveryMethods.shippingLimiting.modals.products.hiddenProduct',
                    )}
                  </Text>
                </Label>
              </Box>
            )}
            {!hasDefaultVariant && (
              <VariantList
                variants={variants}
                selectedVariantIds={selectedVariantIds}
                onAdd={onAdd}
                onRemove={onRemove}
              />
            )}
          </ImageItemStratus>
        </Box>
      </Stack>
    </InteractiveList.CheckboxItem>
  );
}

export default ProductListItem;
